import { LOCATIONS_ENDPOINT } from "config";
import { StatusCodes } from "http-status-codes";

import { BaseService } from "./base-service";

enum LOCATIONS_ENDPOINTS {
  COUNTRIES = "/countries",
  PHONECODES = "/phonecodes",
  STATES = "/{iso2}/states",
  CITIES = "/{iso2}/states/{state}/cities",
}

export interface GetCountriesResponse {
  iso2: string;
  iso3: string;
  name: string;
  phonecode: string;
}

export class LocationsService extends BaseService {
  constructor() {
    super(LOCATIONS_ENDPOINT);
  }

  /**
   * Fetch the list of countries from /countries
   */
  public async getCountries(): Promise<GetCountriesResponse[]> {
    const url = LOCATIONS_ENDPOINTS.COUNTRIES;
    const response = await this.fetch(url);

    if (response.status === StatusCodes.OK) {
      return await response.json();
    }
    throw new Error("Failed to fetch countries");
  }

  /**
   * Fetch the list of phone codes from /phonecodes
   */
  public async getPhoneCodes(): Promise<string[]> {
    const url = LOCATIONS_ENDPOINTS.PHONECODES;
    const response = await this.fetch(url);

    if (response.status === StatusCodes.OK) {
      return await response.json();
    }
    throw new Error("Failed to fetch phone codes");
  }

  /**
   * Fetch the list of states for a given country iso2 from /{iso2}/states
   */
  public async getStates(iso2: string): Promise<string[]> {
    const url = LOCATIONS_ENDPOINTS.STATES.replace("{iso2}", iso2);
    const response = await this.fetch(url);

    if (response.status === StatusCodes.OK) {
      return await response.json();
    }
    throw new Error(`Failed to fetch states for iso2: ${iso2}`);
  }

  /**
   * Fetch the list of cities for a given iso2 and stateName from /{iso2}/states/{state}/cities
   */
  public async getCities(iso2: string, stateName: string): Promise<string[]> {
    // Encode the state name in case it contains spaces or special characters
    const encodedState = encodeURIComponent(stateName);
    const url = LOCATIONS_ENDPOINTS.CITIES.replace("{iso2}", iso2).replace("{state}", encodedState);

    const response = await this.fetch(url);

    if (response.status === StatusCodes.OK) {
      return await response.json();
    }

    throw new Error(`Failed to fetch cities for iso2: ${iso2}, state: ${stateName}`);
  }
}
